/**
 * Faults that only exist on a route.
 *
 * Every fault in this codebase so far has been a tool fault: something is wrong
 * with a chamber, and the tool says so. A shift report at that tool names it
 * and a repair fixes it.
 *
 * These are different, and they are the ones that get past a fab:
 *
 *     every single visit is in specification
 *     and the product is out of specification
 *
 * There is no bad measurement to find, no alarm to answer, and no tool to fix.
 * The tool that made the problem passes every check it has, because the
 * problem is not in any one visit - it is in the eleven of them.
 *
 * THREE WAYS A ROUTE GOES WRONG WITHOUT A TOOL GOING WRONG
 *
 *   ACCUMULATION   a residue that survives each pass and compounds over the
 *                  next. Planarisation is the classic: CMP removes most of the
 *                  topography under it and passes the rest upward, so a small
 *                  inefficiency becomes a large one by metal eight. It is a
 *                  geometric series, and geometric series surprise people.
 *
 *   AN ACCOUNT     a shared budget that several steps draw on. Every anneal is
 *                  reasonable; the sum is not, and the last step in the flow is
 *                  the one that finds out.
 *
 *   A PATTERN      a tool drifting slowly, sampled once per layer. No point is
 *                  outside its control limits. Eight consecutive points on one
 *                  side of the centreline is a one-in-128 coincidence, so it is
 *                  not a coincidence - and it is invisible unless somebody plots
 *                  the visits against each other rather than against the spec.
 *
 * The competency is the same in all three: STOP LOOKING AT THE STATION. It is
 * the hardest habit to teach, because every instinct a good technician has says
 * to go and check the tool.
 */

import { clamp } from '../../core/math'
import { westernElectricViolations } from '../metrology/MetrologySim'
import { CD_BUDGET_NM, printOnTopography } from '../litho/topography'

// ===========================================================================
// ACCUMULATION — planarisation across a metal stack
// ===========================================================================

/**
 * Step height reduction, as a fraction, for one CMP pass.
 *
 * A polish does not erase the topography it is handed; it reduces it. What
 * survives is deposited over and handed to the next layer, so the stack obeys
 *
 *     R_{n+1} = R_n * (1 - eta) + F
 *
 * with eta the planarisation efficiency and F the topography this layer adds by
 * itself. That is a geometric series, and it converges to F/eta - so the
 * STEADY-STATE range is set by the efficiency, not by any single pass.
 *
 * The consequence is the whole lesson: drop eta from 0.90 to 0.55 and the
 * steady state nearly doubles, while every individual polish still removes the
 * same amount and reports the same in-spec number.
 */
export const NOMINAL_PLANARISATION_EFFICIENCY = 0.9

/**
 * Topography one metal layer adds on its own [nm].
 *
 * The step height between a dense wiring region and an open one, before any
 * polish. Scaled so that a healthy stack settles where CMP-11 actually
 * reports its surface range, in the tens of nanometres.
 */
export const PER_LAYER_TOPOGRAPHY_NM = 86

/**
 * Per-visit specification on what a single CMP pass may leave [nm].
 *
 * As WRITTEN on the recipe sheet - and the writing of it is the fault. It was
 * set from what the tool could comfortably hold rather than from what the
 * scanner could afford, and the gap between those two numbers is exactly the
 * room a worn conditioner has to hide in.
 */
export const CMP_VISIT_SPEC_NM = 80

/**
 * The per-visit specification the route actually requires [nm].
 *
 * Worked backwards from the lithography focus budget: the largest surface
 * range the next layer can still be printed on. Any per-visit limit looser
 * than this is a limit that permits a product failure, and comparing the two
 * numbers is the whole of the lesson.
 */
export const CMP_ROUTE_DERIVED_SPEC_NM = 55

export interface StackVisit {
  layer: number
  /** Topography this layer inherited from the one below [nm]. */
  incomingNm: number
  /** What the polish left [nm]. */
  outgoingNm: number
  /** True when this individual visit met the per-visit specification as written. */
  inSpec: boolean
  /** True when it met the tighter limit the route actually required. */
  meetsRouteRequirement: boolean
}

export interface StackResult {
  visits: StackVisit[]
  /** Topography the last layer prints on [nm]. */
  finalRangeNm: number
  /** Where the geometric series converges [nm]. */
  steadyStateNm: number
  /** CD spread this produces at the scanner [nm]. */
  cdSpreadNm: number
  /** True when every individual polish was inside its own specification. */
  everyVisitInSpec: boolean
  /** True when every polish also met what the route needed. */
  everyVisitMeetsRoute: boolean
  /** True when the finished stack can still be printed on. */
  routeInSpec: boolean
}

/**
 * Run a metal stack and watch the topography compound.
 *
 * Returns both the per-visit record and the route outcome, because the whole
 * point is that they disagree.
 */
export function planarisationStack(
  layers = 10,
  efficiency = NOMINAL_PLANARISATION_EFFICIENCY,
  perLayerNm = PER_LAYER_TOPOGRAPHY_NM,
): StackResult {
  const visits: StackVisit[] = []
  let range = 0
  for (let layer = 1; layer <= layers; layer++) {
    const incomingNm = range + perLayerNm
    const outgoingNm = incomingNm * (1 - clamp(efficiency, 0, 1))
    visits.push({
      layer,
      incomingNm,
      outgoingNm,
      // The polish is judged on what IT left, against the number on the
      // recipe sheet. That is the only check anyone runs.
      inSpec: outgoingNm <= CMP_VISIT_SPEC_NM,
      // And against the number the route actually needed, which nobody wrote
      // down. The two verdicts differ, and the difference is the fault.
      meetsRouteRequirement: outgoingNm <= CMP_ROUTE_DERIVED_SPEC_NM,
    })
    range = outgoingNm
  }

  const eta = clamp(efficiency, 1e-6, 1)
  const steadyStateNm = (perLayerNm * (1 - eta)) / eta
  const cd = printOnTopography(range)

  return {
    visits,
    finalRangeNm: range,
    steadyStateNm,
    cdSpreadNm: cd.cdRangeNm,
    everyVisitInSpec: visits.every((v) => v.inSpec),
    everyVisitMeetsRoute: visits.every((v) => v.meetsRouteRequirement),
    routeInSpec: cd.cdRangeNm <= CD_BUDGET_NM,
  }
}

// ===========================================================================
// AN ACCOUNT — thermal budget across a flow
// ===========================================================================

/** Per-step ceiling a single anneal is allowed [equivalent seconds at 1000 C]. */
export const ANNEAL_VISIT_SPEC_S = 900

/**
 * Junction depth after a sequence of anneals [nm].
 *
 * Diffusion length goes as the square root of Dt, and Dt is additive across
 * steps - so junction depth grows as the square root of the TOTAL, and no
 * individual anneal can be blamed for it. Everything before the last step has
 * already spent the budget the last step needed.
 */
export function junctionAfter(startNm: number, thermalSecondsTotal: number): number {
  // sqrt(Dt) scaling with the diffusivity folded into the constant, so the
  // number is in nanometres against seconds at 1000 C.
  return startNm + 1.7 * Math.sqrt(Math.max(thermalSecondsTotal, 0))
}

export interface BudgetVisit {
  label: string
  seconds: number
  /** Cumulative budget spent up to and including this step. */
  cumulativeSeconds: number
  junctionNm: number
  /** True when this step, on its own, is a reasonable anneal. */
  inSpec: boolean
}

export interface BudgetResult {
  visits: BudgetVisit[]
  totalSeconds: number
  finalJunctionNm: number
  junctionLimitNm: number
  everyVisitInSpec: boolean
  routeInSpec: boolean
  /** How much budget was left when the last step ran. */
  remainingAtLastStepS: number
}

export function thermalAccount(
  steps: Array<{ label: string; seconds: number }>,
  startJunctionNm = 24,
  junctionLimitNm = 78,
): BudgetResult {
  const visits: BudgetVisit[] = []
  let cumulative = 0
  for (const s of steps) {
    cumulative += s.seconds
    visits.push({
      label: s.label,
      seconds: s.seconds,
      cumulativeSeconds: cumulative,
      junctionNm: junctionAfter(startJunctionNm, cumulative),
      inSpec: s.seconds <= ANNEAL_VISIT_SPEC_S,
    })
  }
  const total = cumulative
  const finalJunctionNm = junctionAfter(startJunctionNm, total)
  // What the last step had left: the budget that would still have kept the
  // junction legal, minus what everything before it had already taken.
  const budgetForLimit = Math.pow((junctionLimitNm - startJunctionNm) / 1.7, 2)
  const beforeLast = total - (steps[steps.length - 1]?.seconds ?? 0)

  return {
    visits,
    totalSeconds: total,
    finalJunctionNm,
    junctionLimitNm,
    everyVisitInSpec: visits.every((v) => v.inSpec),
    routeInSpec: finalJunctionNm <= junctionLimitNm,
    remainingAtLastStepS: budgetForLimit - beforeLast,
  }
}

// ===========================================================================
// A PATTERN — a slow drift, sampled once per layer
// ===========================================================================

export interface DriftVisit {
  layer: number
  value: number
  /** True when this reading is inside the per-visit specification. */
  inSpec: boolean
  /** True when it is inside the statistical control limits. */
  inControl: boolean
}

export interface DriftResult {
  visits: DriftVisit[]
  centre: number
  sigma: number
  upperControl: number
  lowerControl: number
  specHalfWidth: number
  /** Rules that fired, and where. */
  violations: Array<{ index: number; rule: string }>
  everyVisitInSpec: boolean
  everyVisitInControl: boolean
  /** True when the SERIES says something no single point does. */
  patternDetected: boolean
  /** Total drift across the route [same units as value]. */
  totalDriftNm: number
}

/**
 * A tool measured once per layer, drifting slowly underneath.
 *
 * Deterministic by default, because the lesson is about the PATTERN and a
 * lesson that reads differently every time it is run is not a lesson. The noise
 * is a fixed pseudo-random sequence, so the chart is the same chart for every
 * student and an instructor can point at a specific point on it.
 */
export function driftAcrossLayers(opts: {
  layers?: number
  target: number
  driftPerLayer: number
  noiseSigma: number
  /**
   * Sigma the control limits were set from.
   *
   * Separate from the run-to-run noise on purpose. A chart is set up from a
   * characterisation that saw sources this short run does not - lot to lot,
   * chamber to chamber, week to week - so its limits are wider than the
   * scatter of eleven consecutive wafers. That gap is real, it is why a slow
   * drift fits inside the limits, and pretending otherwise would make the
   * lesson easier than the job.
   */
  controlSigma?: number
  specHalfWidth: number
  seed?: number
}): DriftResult {
  const layers = opts.layers ?? 11
  let seed = opts.seed ?? 20260827
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff
    return seed / 0x7fffffff
  }
  const normal = () => {
    const u1 = Math.max(rand(), 1e-12)
    const u2 = rand()
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)
  }

  const values: number[] = []
  for (let i = 0; i < layers; i++) {
    values.push(opts.target + opts.driftPerLayer * i + normal() * opts.noiseSigma)
  }

  // Control limits come from the process as it was CHARACTERISED - the noise
  // alone - not from the drifting series. Deriving them from the drifting data
  // would widen the limits until the drift fitted inside them, which is a real
  // and common way to lose a signal, and not the one being taught here.
  const centre = opts.target
  const sigma = opts.controlSigma ?? opts.noiseSigma
  const upperControl = centre + 3 * sigma
  const lowerControl = centre - 3 * sigma

  const violations = westernElectricViolations(values, centre, sigma)
  const visits: DriftVisit[] = values.map((value, i) => ({
    layer: i + 1,
    value,
    inSpec: Math.abs(value - opts.target) <= opts.specHalfWidth,
    inControl: value <= upperControl && value >= lowerControl,
  }))

  return {
    visits,
    centre,
    sigma,
    upperControl,
    lowerControl,
    specHalfWidth: opts.specHalfWidth,
    violations,
    everyVisitInSpec: visits.every((v) => v.inSpec),
    everyVisitInControl: visits.every((v) => v.inControl),
    // A rule that is NOT "one point beyond 3 sigma" is by definition something
    // only the series could have told you.
    patternDetected: violations.some((v) => v.rule !== '1 point beyond 3σ'),
    totalDriftNm: opts.driftPerLayer * (layers - 1),
  }
}

// ===========================================================================

export type RouteFaultId = 'planarisation-drift' | 'thermal-account' | 'slow-drift'

export interface RouteFaultCase {
  id: RouteFaultId
  label: string
  /** What is actually wrong. Never shown before the student commits. */
  truth: string
  /** What the tool says, every single visit. */
  toolSays: string
  /** What the product says at the end. */
  productSays: string
  /** The move that finds it. */
  howToSeeIt: string
}

export const ROUTE_FAULTS: RouteFaultCase[] = [
  {
    id: 'planarisation-drift',
    label: 'Metal 8 will not print, and CMP-11 is in spec on every layer',
    truth:
      'The pad conditioner is worn, so planarisation efficiency has fallen from 0.90 to 0.55. ' +
      'Each polish still removes what the recipe asks for and still leaves less than the 45 nm ' +
      'per-visit limit — but it passes more topography upward than it used to, and ten layers ' +
      'of that compounds.',
    toolSays:
      'Every CMP run on every layer is inside its own specification. Removal rate, uniformity ' +
      'and post-polish range all read normal.',
    productSays:
      'CD spread at the scanner is past the focus budget by metal 8, and lithography is ' +
      'blaming the scanner.',
    howToSeeIt:
      'Plot the post-CMP range against LAYER NUMBER rather than against the specification. A ' +
      'healthy stack converges to a flat line; this one climbs and keeps climbing, because a ' +
      'geometric series with a smaller ratio converges somewhere higher.',
  },
  {
    id: 'thermal-account',
    label: 'The junction is too deep and no anneal was too long',
    truth:
      'The well drive-in was extended to fix a resistivity complaint. It is still a legal ' +
      'anneal on its own. It spent budget the spike anneal at the end of the flow was ' +
      'depending on, and the junction that comes out is 30% too deep.',
    toolSays:
      'Every furnace step is inside its own time and temperature window. THRM-04 diagnoses ' +
      'clean at every visit.',
    productSays: 'Junction depth out of specification at the end of the gate module.',
    howToSeeIt:
      'Add the thermal budget up along the route instead of checking each step against its own ' +
      'limit. The last anneal in a flow is constrained by every anneal before it, and the ' +
      'account has no owner.',
  },
  {
    id: 'slow-drift',
    label: 'Eleven measurements, all in spec, and the tool is broken',
    truth:
      'A mass flow controller is drifting about 0.4 nm-equivalent per layer. Over eleven ' +
      'layers that is a 4 nm shift — inside the specification at every single visit, and a ' +
      'process that is no longer centred.',
    toolSays: 'Every reading is inside spec and inside the control limits. No alarm has fired.',
    productSays:
      'Parametric yield is sliding and nobody can point at a lot where it went wrong.',
    howToSeeIt:
      'The Western Electric rules. Eight consecutive points on one side of the centreline is a ' +
      'one-in-128 event on a centred process, so it is not chance. Nothing is out of limits; ' +
      'the PATTERN is the signal, and only a chart across visits has one.',
  },
]
